"use client";

import React from "react";
import { PageContainer } from "@/src/components/layout/page-container";
import { Section } from "@/src/components/layout/section";
import { SectionCard } from "@/src/components/container/section-card";
import { CardTitle } from "@/src/components/container/card";
import { Text } from "@/src/components/information/text";
import { Badge } from "@/src/components/information/badge";

export default function BoilerplateGuidePage() {
  return (
    <PageContainer
      size="xl"
      className="py-theme-6 px-theme-6 space-y-theme-5 overflow-auto h-full"
    >
      {/* Header */}
      <div className="space-y-2">
        <Text as="h2" className="text-2xl font-bold tracking-tight">
          BlueCopa UI Boilerplate Guide
        </Text>
        <Text as="p" className="text-base text-muted-foreground">
          Integration guide for consuming the BlueCopa design system in your
          finance application
        </Text>
      </div>

      {/* Getting Started */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Getting Started</CardTitle>
            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2">Install shadcn CLI:</Text>
                <pre className="bg-muted p-3 rounded-md text-sm overflow-x-auto">
                  <code>npm install -D shadcn-ui</code>
                </pre>
              </div>
              <div>
                <Text className="font-semibold mb-2">
                  Add components from registry:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-sm overflow-x-auto">
                  <code>
                    npx shadcn@latest add --all --registry &lt;registry-url&gt;
                  </code>
                </pre>
              </div>
              <div>
                <Text className="font-semibold mb-2">
                  Registry Setup Pattern:
                </Text>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Components copied to `src/components/ui/bluecopa/ui/`</li>
                  <li>Tailwind CSS integration automatic</li>
                  <li>CSS custom properties added to globals</li>
                  <li>Full TypeScript support included</li>
                </ul>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Import Patterns */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Import Patterns</CardTitle>
            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2 text-success">
                  CORRECT:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-sm overflow-x-auto">
                  <code>{`import { Button } from "@/components/ui/bluecopa/ui/button"
import { Input } from "@/components/ui/bluecopa/ui/input"
import { Scaffold, PageContainer } from "@/components/ui/bluecopa/ui/layout"
import { ChartProvider, BarChart } from "@/components/ui/bluecopa/ui/charts"`}</code>
                </pre>
              </div>
              <div>
                <Text className="font-semibold mb-2 text-destructive">
                  NEVER DO THIS:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-sm overflow-x-auto">
                  <code>{`import { Button } from "shadcn-ui/button"
import { Button } from "@/components/button"
import { Chart } from "recharts"`}</code>
                </pre>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Container Hierarchy */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Container Hierarchy</CardTitle>
            <Text className="text-sm text-muted-foreground mb-3">
              Every page must follow this exact nesting structure:
            </Text>
            <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto">
              <code>{`Scaffold (root)
├── Sidebar (prop)
├── Topbar (prop)
└── PageContainer
    ├── PageHeader (optional)
    └── Section
        └── SectionCard
            └── Content`}</code>
            </pre>
            <div className="mt-4 space-y-2">
              <Text className="font-semibold">Key Rules:</Text>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Scaffold is the root container</li>
                <li>Sidebar and Topbar are props (not children)</li>
                <li>PageContainer is direct child of Scaffold</li>
                <li>Never nest multiple PageContainers</li>
                <li>Content wrapped in Section → SectionCard</li>
              </ul>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Controller Pattern */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Controller Pattern</CardTitle>
            <Text className="text-sm text-muted-foreground mb-3">
              Separate business logic from UI rendering
            </Text>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2">Create Controller Hook:</Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`// hooks/useDashboardController.ts
export interface DashboardState {
  kpis: KPI[]
  loading: boolean
  error: Error | null
}

export function useDashboardController() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["kpis"],
    queryFn: fetchKPIs
  })

  return {
    kpis: data || [],
    loading: isLoading,
    error: error || null
  }
}`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2">Use in View Component:</Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`// pages/dashboard.tsx
export function DashboardPage() {
  const { kpis, loading } = useDashboardController()

  if (loading) return <DashboardSkeleton />

  return (
    <Scaffold sidebar={<Sidebar />} topbar={<Topbar />}>
      <PageContainer>
        {/* Render kpis here */}
      </PageContainer>
    </Scaffold>
  )
}`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2">Benefits:</Text>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Testability — test logic separately from UI</li>
                  <li>Reusability — share logic across views</li>
                  <li>Maintainability — changes don't affect components</li>
                  <li>Type Safety — explicit interfaces</li>
                </ul>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Theming */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Theming</CardTitle>
            <Text className="text-sm text-muted-foreground mb-3">
              BlueCopa includes 9 built-in themes with full customization
            </Text>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2">Built-in Themes:</Text>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Badge variant="default">Modern (default)</Badge>
                  <Badge variant="default">Sharp</Badge>
                  <Badge variant="default">Light</Badge>
                  <Badge variant="default">Dark</Badge>
                  <Badge variant="default">Minimal</Badge>
                  <Badge variant="default">Elevated</Badge>
                  <Badge variant="default">Glass</Badge>
                  <Badge variant="default">Neumorphic</Badge>
                  <Badge variant="default">High Contrast</Badge>
                </div>
              </div>

              <div>
                <Text className="font-semibold mb-2">Apply Theme:</Text>
                <pre className="bg-muted p-3 rounded-md text-sm overflow-x-auto">
                  <code>{"<html class=\"theme-glass\">"}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2">
                  Theme Token Classes:
                </Text>
                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                  <code>{`// Spacing
<div className="p-theme-6 gap-theme-4 mb-theme-8">

// Border Radius
<div className="rounded-theme-md">

// Shadows
<div className="shadow-theme">

// Colors (use CSS variables)
<div className="text-[var(--color-primary)]">`}</code>
                </pre>
              </div>

              <div>
                <Text className="font-semibold mb-2">Dark Mode:</Text>
                <pre className="bg-muted p-3 rounded-md text-sm overflow-x-auto">
                  <code>
                    {`document.documentElement.classList.add("dark")`}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Key Components */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Key Component Categories</CardTitle>

            <div className="grid gap-theme-4">
              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Input Components:
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Button, Input, Select, Checkbox, Radio, Switch, Calendar,
                  DatePicker, Combobox, FormField
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Navigation Components:
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Tabs, Breadcrumb, Pagination, Stepper, Sidebar, Topbar,
                  TreeMenu
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Information Components:
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Badge, Avatar, Alert, Toast, Tooltip, Progress, Spinner,
                  Skeleton, KPICard
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Container Components:
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Card, Dialog, Sheet, SidePanel, Popover, ScrollArea,
                  Accordion, Kanban
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Data Components:
                </Text>
                <Text className="text-sm text-muted-foreground">
                  DataTable (&lt;10K rows), AGGridTable (&gt;10K rows),
                  ListingView
                </Text>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm">
                  Charts (ECharts only):
                </Text>
                <Text className="text-sm text-muted-foreground">
                  BarChart, LineChart, AreaChart, PieChart, DonutChart,
                  WaterfallChart, GaugeChart, TreemapChart
                </Text>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Best Practices */}
      <Section>
        <SectionCard>
          <div className="space-y-theme-4">
            <CardTitle className="text-lg">Best Practices</CardTitle>

            <div className="space-y-theme-3">
              <div>
                <Text className="font-semibold mb-2 text-sm text-success">
                  ALWAYS:
                </Text>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Use theme token classes (p-theme-6, gap-theme-4)</li>
                  <li>Wrap form inputs in FormField</li>
                  <li>Follow layout hierarchy strictly</li>
                  <li>Use controller pattern for state</li>
                  <li>Wrap charts in ChartProvider</li>
                  <li>Export typed interfaces from controllers</li>
                </ul>
              </div>

              <div>
                <Text className="font-semibold mb-2 text-sm text-destructive">
                  NEVER:
                </Text>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Hardcode colors (use theme tokens)</li>
                  <li>Hardcode spacing (use theme classes)</li>
                  <li>Use inline styles (style prop)</li>
                  <li>Mix chart libraries (ECharts only)</li>
                  <li>Put business logic in components</li>
                  <li>Build custom component versions</li>
                </ul>
              </div>
            </div>
          </div>
        </SectionCard>
      </Section>
    </PageContainer>
  );
}

export { BoilerplateGuidePage };
